import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosAdmin from "../../../../api/apiAdmin";
import { successToast, errorToast, warningToast } from "../../toastAlerts/ToastAlerts";
import {
  BsSearch,
  BsTruck,
  BsClipboardCheck,
  BsX,
  BsArrowClockwise,
  BsCalendarWeek,
  BsPencilSquare,
  BsExclamationTriangle,
  BsCheckCircle,
  BsPrinter,
} from "react-icons/bs";
import { apiError } from "../../../../api/apiError";
import OnWayLabels from "../Packages/AppeardPackages/OnWayLabels";

/**
 * Admin console for the OnWay courier — covers the whole integration surface
 * documented at onway.ge (integrations_id=4):
 *
 *   order/add            → dispatch a pack (full payload editor)
 *   order/edit           → modify a live order
 *   order/edit + 47      → cancel
 *   order/datesinfo      → pickup / delivery date preview
 *   order/regions        → city picker (1500+ zones, id-resolved)
 *   order/getuserinvoice → is invoice payment enabled for our account
 *   order/getusercod     → is COD enabled for our account
 *
 * The payment method / payer pair is constrained by OnWay's finance policy —
 * the legal combinations come from /admin/onway/options so the dropdowns can
 * never produce a rejected order.
 */

/** [payment, payer] → is this pair legal given the COD state? */
const comboAllowed = (combos, payment, payer) =>
  (combos || []).some(([m, p]) => m === Number(payment) && p === Number(payer));

const OnWayPage = () => {
  const { t } = useTranslation();

  const [q, setQ] = useState("");
  const [packs, setPacks] = useState([]);
  const [loadingPacks, setLoadingPacks] = useState(false);

  // { pack, payload, mode: "send" | "edit", order? }
  const [editor, setEditor] = useState(null);
  // Other parcels of the same customer that could share this courier order.
  const [siblings, setSiblings] = useState(null);
  const [rideAlong, setRideAlong] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [orders, setOrders] = useState([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersSummary, setOrdersSummary] = useState(null);
  // Filters over the order log. `state` splits live / cancelled / failed, where
  // failed means OnWay never returned a tracking number for it.
  const [orderFilters, setOrderFilters] = useState({
    q: "",
    state: "",
    status: "",
    cod: "",
    from: "",
    to: "",
  });
  const [expanded, setExpanded] = useState(null);
  // Orders whose labels are being printed; also drives the bulk selection.
  const [labelIds, setLabelIds] = useState(null);
  const [labelFilters, setLabelFilters] = useState(null);
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersLastPage, setOrdersLastPage] = useState(1);
  // Default is newest-placed; 'updated' surfaces whatever OnWay touched last,
  // which is what matters when watching for failures.
  const [ordersSort, setOrdersSort] = useState("id");
  // Unpaid parcels are hidden unless asked for, because attaching one forces
  // COD — the courier collects at the door instead of the customer paying us.
  const [includeUnpaid, setIncludeUnpaid] = useState(false);
  const [cod, setCod] = useState(false);
  const [picked, setPicked] = useState({});
  const [options, setOptions] = useState(null);
  const [account, setAccount] = useState(null);
  const [regions, setRegions] = useState([]);

  // Dates tool
  const [dates, setDates] = useState({ from: "", to: "", result: null });

  // Money between us and OnWay for a period: the shipping they bill us on
  // every delivered order, and the cash their courier collected at the door
  // for us. Figures come from their delivery callback, i.e. what their
  // statement will say. Defaults to the current month.
  const monthStart = new Date();
  monthStart.setDate(1);
  const iso = (d) => d.toISOString().slice(0, 10);
  const [settle, setSettle] = useState({ from: iso(monthStart), to: iso(new Date()), data: null, busy: false, open: false });
  const loadSettlement = async () => {
    setSettle((x) => ({ ...x, busy: true }));
    try {
      const { data } = await axiosAdmin.get(`/onway/settlement`, { params: { from: settle.from, to: settle.to } });
      setSettle((x) => ({ ...x, data, busy: false }));
    } catch (e) {
      setSettle((x) => ({ ...x, busy: false }));
      errorToast(apiError(e, t("admin_error")));
    }
  };
  const downloadSettlement = async () => {
    try {
      const res = await axiosAdmin.get(`/onway/settlement`, { params: { from: settle.from, to: settle.to, format: "xlsx" }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.setAttribute("download", `onway-settlement-${settle.from}_${settle.to}.xlsx`);
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) {
      errorToast(apiError(e, t("admin_error")));
    }
  };
  const gel = (n) => `${Number(n || 0).toFixed(2)} ₾`;

  const statusMap = options?.statuses || {};

  /* ------------------------------------------------------------------ load */

  const loadOrders = useCallback(async () => {
    try {
      const params = { per_page: 100, page: ordersPage, sort: ordersSort };
      Object.entries(orderFilters).forEach(([k, v]) => {
        if (v !== "" && v !== null) params[k] = v;
      });
      const { data } = await axiosAdmin.get(`/onway/orders`, { params });
      setOrders(data.data || []);
      setOrdersTotal(data.total ?? (data.data || []).length);
      setOrdersLastPage(data.last_page ?? 1);
    } catch (err) {
      errorToast(apiError(err, t("admin_error_try_again")));
    }
  }, [orderFilters, ordersPage, ordersSort, t]);

  // Any filter change starts from page one — staying on page 7 of a result set
  // that now has two pages just shows an empty table.
  useEffect(() => {
    setOrdersPage(1);
  }, [orderFilters]);

  // Counts over the whole table, so the chips don't lie about what's out there.
  const loadOrdersSummary = useCallback(async () => {
    try {
      const { data } = await axiosAdmin.get(`/onway/orders/summary`);
      setOrdersSummary(data);
    } catch {}
  }, []);

  const loadRegions = useCallback(async (refresh = false) => {
    try {
      const { data } = await axiosAdmin.get(
        `/onway/regions${refresh ? "?refresh=1" : ""}`
      );
      setRegions(Array.isArray(data) ? data : []);
      if (refresh) successToast(`${data?.length || 0} ქალაქი განახლდა`);
    } catch {}
  }, []);

  useEffect(() => {
    loadOrders();
    loadOrdersSummary();
    loadRegions();
    axiosAdmin
      .get(`/onway/options`)
      .then(({ data }) => setOptions(data))
      .catch(() => {});
    axiosAdmin
      .get(`/onway/account`)
      .then(({ data }) => setAccount(data))
      .catch(() => {});
  }, [loadOrders, loadOrdersSummary, loadRegions]);

  const searchPacks = async () => {
    setLoadingPacks(true);
    try {
      const { data } = await axiosAdmin.get(`/onway/packs`, {
        params: { q, per_page: 20, paid: includeUnpaid ? 0 : 1 },
      });
      setPacks(data.data || []);
    } catch (err) {
      errorToast(apiError(err, t("admin_error")));
    }
    setLoadingPacks(false);
  };

  /* ---------------------------------------------------------------- editor */

  const openSend = async (pack) => {
    try {
      const { data } = await axiosAdmin.get(`/onway/packs/${pack.id}/preview`);
      setEditor({ pack, payload: data, mode: "send" });
      setSiblings(null);
      setRideAlong([]);

      // Sending one parcel when the customer has three waiting means three
      // courier fees for one doorstep — so offer the rest straight away.
      axiosAdmin
        .get(`/onway/packs/${pack.id}/siblings`, {
          params: { paid: includeUnpaid ? 0 : 1 },
        })
        .then(({ data: sib }) => setSiblings(sib))
        .catch(() => setSiblings(null));
    } catch (err) {
      errorToast(apiError(err, t("admin_error")));
    }
  };

  const openEdit = (order) => {
    setSiblings(null);
    setRideAlong([]);
    return setEditor({
      pack: order.pack || { id: order.pack_id, tracking_number: "—" },
      payload: { ...(order.payload || {}) },
      mode: "edit",
      order,
    });
  };

  // The form must reflect what will actually be sent: weight, quantity and
  // order_number all describe the SET of parcels, so ticking a sibling has to
  // move them. The server recomputes these too — this is what makes the
  // operator's preview honest, not what makes it correct.
  useEffect(() => {
    if (!editor || editor.mode !== "send" || !siblings?.anchor) return;

    const chosen = (siblings.siblings || []).filter((x) => rideAlong.includes(x.id));
    const all = [siblings.anchor, ...chosen];

    setEditor((prev) => {
      if (!prev) return prev;
      // quantity is NOT touched: it counts the packages the courier carries,
      // not the parcels inside them, and we normally repack a customer's
      // parcels into one box. It stays the operator's to set.
      const next = {
        ...prev.payload,
        weight: Number(
          all.reduce((a, x) => a + Number(x.chargeable || 0), 0).toFixed(2)
        ),
        order_number: all.map((x) => x.tracking_number).join(", "),
      };

      // COD is the total owed across everything on the order.
      const owed = Number(
        all.reduce((a, x) => a + Math.max(0, Number(x.outstanding || 0)), 0).toFixed(2)
      );
      next.order_price = cod && owed > 0 ? owed : 0;
      if (cod && owed > 0) {
        next.payment = 1;
        next.payer = 2;
      }

      return { ...prev, payload: next };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rideAlong, siblings, cod]);

  const patch = (key, value) =>
    setEditor((p) => ({ ...p, payload: { ...p.payload, [key]: value } }));

  const toggleService = (id) =>
    setEditor((p) => {
      const cur = Array.isArray(p.payload.services) ? p.payload.services : [];
      const next = cur.map(Number).includes(Number(id))
        ? cur.filter((s) => Number(s) !== Number(id))
        : [...cur, Number(id)];
      return { ...p, payload: { ...p.payload, services: next } };
    });

  const payload = editor?.payload || {};
  const isCod = Number(payload.order_price || 0) > 0;

  /** Mirror of the backend's payment-policy rules, so the operator sees the
   *  problem before spending a round-trip on it. */
  const clientErrors = useMemo(() => {
    if (!editor || !options) return [];
    const errs = [];
    const combos = isCod ? options.cod_combinations : options.combinations;

    if (!comboAllowed(combos, payload.payment, payload.payer)) {
      errs.push(
        isCod
          ? "COD-იან შეკვეთაზე დაშვებულია მხოლოდ: ნაღდი/მიმღები, ტერმინალი/მიმღები, ინვოისი/შემკვეთი"
          : "დაუშვებელი კომბინაცია — იხ. გადახდის პოლიტიკა"
      );
    }
    if (Number(payload.receiver_shipprice_pay) === 1) {
      if (!isCod) errs.push("„იხდის მიმღები“ მოითხოვს COD თანხას");
      else if (
        !comboAllowed(
          options.receiver_shipprice_combinations,
          payload.payment,
          payload.payer
        )
      )
        errs.push("„იხდის მიმღები“ მუშაობს მხოლოდ ნაღდი/მიმღები ან ტერმინალი/მიმღები-ზე");
    }
    if (Number(payload.payment) === 3 && account?.invoice_enabled === false)
      errs.push("ინვოისით გადახდა გამორთულია ჩვენს OnWay ანგარიშზე");
    if (isCod && account?.cod_enabled === false)
      errs.push("COD გამორთულია ჩვენს OnWay ანგარიშზე");

    return errs;
  }, [editor, options, account, payload, isCod]);

  const submit = async () => {
    if (!editor) return;
    setSubmitting(true);
    try {
      const url =
        editor.mode === "send"
          ? `/onway/packs/${editor.pack.id}/send`
          : `/onway/orders/${editor.order.id}/edit`;
      const { data } = await axiosAdmin.post(url, {
        overrides: editor.payload,
        ...(editor.mode === "send" ? { cod } : {}),
        ...(editor.mode === "send" && rideAlong.length
          ? { pack_ids: rideAlong }
          : {}),
      });

      successToast(
        editor.mode === "send"
          ? `OnWay tracking: ${data.tracking || "(pending)"}` +
            (data.pack_count > 1 ? ` · ${data.pack_count} ამანათი` : "") +
            (Number(data.balance_charged) > 0
              ? ` · ბალანსიდან ჩამოიჭრა ${Number(data.balance_charged).toFixed(2)} ₾`
              : "")
          : "შეკვეთა განახლდა"
      );
      if (data?.warning) warningToast(data.warning);
      setEditor(null);
      loadOrders();
    } catch (e) {
      // Backend returns 422 with { error, errors[] } for policy violations.
      const d = e?.response?.data;
      // An unpaid parcel is refused unless COD is on — offer it rather than
      // leaving the operator to work out what "needs COD" means.
      if (d?.needs_cod) {
        setCod(true);
        errorToast(`${d.error} — COD ჩაირთო, სცადეთ ხელახლა`);
      } else {
        errorToast(apiError(e, d?.errors?.join(" · ") || d?.error || t("admin_error")));
      }
    }
    setSubmitting(false);
  };

  const cancelOrder = async (order) => {
    if (!window.confirm(`Cancel OnWay order ${order.tracking || "(no tracking)"}?`))
      return;
    try {
      const { data } = await axiosAdmin.post(`/onway/orders/${order.id}/cancel`);
      if (data?.warning) warningToast(data.warning);
      else successToast("შეკვეთა გაუქმდა");
      loadOrders();
    } catch (e) {
      // OnWay refuses cancels once an order has moved on — surface their reason.
      errorToast(apiError(e, e?.response?.data?.error || t("admin_error")));

      // Their refusal reads the same whether the order has progressed or was
      // never registered at all. When it is the latter, cancel can never
      // succeed and the parcels stay blocked forever — so offer to close it on
      // our side, but only after the operator has checked with OnWay.
      const detach = window.confirm(
        `OnWay-მ უარყო გაუქმება.\n\n` +
          `თუ ეს შეკვეთა OnWay-სთან საერთოდ არ არსებობს, შეგვიძლია დავხუროთ ` +
          `მხოლოდ ჩვენს მხარეს და ამანათები გავათავისუფლოთ.\n\n` +
          `⚠️ თუ შეკვეთა მათთან რეალურად არსებობს, კურიერი მაინც მივა.\n` +
          `დარწმუნებული ხართ, რომ OnWay-სთან არ არსებობს?`
      );
      if (!detach) return;

      const reason = window.prompt(
        "მიზეზი (რჩება აუდიტში, მინიმუმ 5 სიმბოლო):",
        `OnWay-სთან არ არსებობს — თრექინგი ${order.tracking || "?"}`
      );
      if (!reason || reason.trim().length < 5) return;

      try {
        const { data } = await axiosAdmin.post(
          `/onway/orders/${order.id}/detach`,
          { reason: reason.trim() }
        );
        successToast(
          `დაიხურა ჩვენს მხარეს — გათავისუფლდა ${data.packs_freed} ამანათი`
        );
        loadOrders();
      } catch (err) {
        errorToast(apiError(err, "ლოკალურად დახურვა ვერ მოხერხდა"));
      }
    }
  };

  // One parcel was not put in the bag. It leaves the order (which stays live
  // and keeps its price — OnWay will not let us change it) so it can be sent
  // again with whatever else of that customer's is still on the shelf.
  const removePack = async (order, pack) => {
    const ok = window.confirm(
      `${pack.tracking_number} მოიხსნება შეკვეთიდან ${order.tracking}.\n\n` +
        `შეკვეთა და მისი ფასი უცვლელი რჩება (OnWay ცვლილებას არ უშვებს). ` +
        `თუ შეკვეთა COD-ია, ამ ამანათის თანხას კურიერი მაინც ამ შეკვეთაზე აიღებს — ახალში აღარ დაერიცხება.\n\n` +
        `ამანათი ხელახლა გასაგზავნი გახდება. გავაგრძელო?`
    );
    if (!ok) return;
    const reason = window.prompt("მიზეზი (რჩება ისტორიაში):", "პარკში არ ჩაიდო");
    if (reason === null) return;
    try {
      const { data } = await axiosAdmin.post(
        `/onway/orders/${order.id}/packs/${pack.id}/remove`,
        { reason: reason.trim() }
      );
      successToast(data.message || "ამანათი მოიხსნა");
      loadOrders();
    } catch (err) {
      errorToast(apiError(err, err?.response?.data?.error || "მოხსნა ვერ მოხერხდა"));
    }
  };

  // A row OnWay "accepted" without a tracking number. Usually the order
  // already existed under that order_number and this row is a duplicate;
  // the server closes it and points at the real one. Otherwise it asks
  // OnWay's api/order/info — which refuses until they enable it for us —
  // and then the operator can paste the tracking from OnWay's panel.
  const resolveTracking = async (order, manual) => {
    try {
      const { data } = await axiosAdmin.post(
        `/onway/orders/${order.id}/resolve-tracking`,
        manual ? { tracking: manual } : {}
      );
      successToast(data.message || `თრექინგი: ${data.tracking}`);
      loadOrders();
    } catch (err) {
      const msg = err?.response?.data?.error || "ვერ მოიძებნა";
      if (manual) {
        errorToast(msg);
        return;
      }
      const typed = window.prompt(
        `${msg}\n\nთუ OnWay-ის პანელში ხედავთ, ჩაწერეთ თრექინგი (მხოლოდ ციფრები). ცარიელი = გაუქმება.`,
        ""
      );
      if (typed && typed.trim()) resolveTracking(order, typed.trim());
    }
  };

  // OnWay never created this one (deduplicated, or refused silently): close
  // it on our side so the parcels stop being counted as booked.
  const closeLocally = async (order) => {
    const reason = window.prompt(
      "OnWay-სთან ეს შეკვეთა არ არსებობს — დაიხურება მხოლოდ ჩვენს მხარეს.\nმიზეზი (რჩება აუდიტში, მინიმუმ 5 სიმბოლო):",
      `OnWay-სთან არ არსებობს — თრექინგი არ დაბრუნებულა`
    );
    if (!reason || reason.trim().length < 5) return;
    try {
      const { data } = await axiosAdmin.post(`/onway/orders/${order.id}/detach`, {
        reason: reason.trim(),
      });
      successToast(`დაიხურა ჩვენს მხარეს — გათავისუფლდა ${data.packs_freed} ამანათი`);
      loadOrders();
    } catch (err) {
      errorToast(apiError(err, "ლოკალურად დახურვა ვერ მოხერხდა"));
    }
  };

  const runDates = async () => {
    if (!dates.from || !dates.to) return;
    try {
      const { data } = await axiosAdmin.post(`/onway/dates`, {
        from_city_id: Number(dates.from),
        to_city_id: Number(dates.to),
      });
      setDates((d) => ({ ...d, result: data.response ?? data }));
    } catch (e) {
      errorToast(apiError(e, e?.response?.data?.error || t("admin_error")));
    }
  };

  const statusLabel = (id) => (id != null ? statusMap[id] || `#${id}` : "—");

  /* ------------------------------------------------------------------- ui */

  const field = (key, label, extra = {}) => (
    <label key={key} className="text-xs flex flex-col gap-1">
      <span className="text-slate-500 dark:text-slate-400">
        {label} <span className="text-slate-400 font-mono">({key})</span>
      </span>
      <input
        className="h-9 px-3 border border-slate-300 dark:border-slate-600 rounded-md
                   dark:bg-slate-700 dark:text-white"
        value={payload?.[key] ?? ""}
        onChange={(e) => patch(key, e.target.value)}
        {...extra}
      />
    </label>
  );

  const cityField = (key, label) => (
    <label className="text-xs flex flex-col gap-1">
      <span className="text-slate-500 dark:text-slate-400">
        {label} <span className="text-slate-400 font-mono">({key})</span>
      </span>
      <input
        list="onway-regions"
        className="h-9 px-3 border border-slate-300 dark:border-slate-600 rounded-md
                   dark:bg-slate-700 dark:text-white"
        value={payload?.[key] ?? ""}
        onChange={(e) => patch(key, e.target.value)}
        placeholder="აირჩიეთ ან აკრიფეთ…"
      />
    </label>
  );

  const selectField = (key, label, map) => (
    <label className="text-xs flex flex-col gap-1">
      <span className="text-slate-500 dark:text-slate-400">
        {label} <span className="text-slate-400 font-mono">({key})</span>
      </span>
      <select
        className="h-9 px-3 border border-slate-300 dark:border-slate-600 rounded-md
                   dark:bg-slate-700 dark:text-white"
        value={payload?.[key] ?? ""}
        onChange={(e) => patch(key, e.target.value)}
      >
        {Object.entries(map || {}).map(([id, name]) => (
          <option key={id} value={id}>
            {name} ({id})
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div
      className="xl:ml-[280px] mt-[55px] bg-slate-100 dark:bg-slate-900
                 min-h-screen p-6 sm:p-3 pb-24"
    >
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <BsTruck className="text-2xl text-teal_600" />
        <h1 className="text-xl font-bold dark:text-white">OnWay Dispatch</h1>

        {/* Account capability badges — the docs require checking these before
            sending invoice-paid or COD orders. */}
        {account && (
          <div className="flex gap-2 ml-2 text-[11px]">
            <span
              className={`px-2 py-1 rounded-full font-bold ${
                account.invoice_enabled
                  ? "bg-green-100 text-green-700"
                  : "bg-slate-200 text-slate-500"
              }`}
              title="getuserinvoice"
            >
              ინვოისი: {account.invoice_enabled ? "ჩართული" : "გამორთული"}
            </span>
            <span
              className={`px-2 py-1 rounded-full font-bold ${
                account.cod_enabled
                  ? "bg-green-100 text-green-700"
                  : "bg-slate-200 text-slate-500"
              }`}
              title="getusercod"
            >
              COD: {account.cod_enabled ? "ჩართული" : "გამორთული"}
            </span>
          </div>
        )}
      </div>

      {/* Shared city catalogue for every city input on the page. */}
      <datalist id="onway-regions">
        {regions.map((r) => (
          <option key={r.id} value={r.name} />
        ))}
      </datalist>

      {/* Search + pack list */}
      <div className="bg-white dark:bg-slate-700 rounded-xl p-4 mb-6">
        <div className="flex gap-2 mb-3">
          <div className="flex-1 flex items-center gap-2 border border-slate-300 rounded-md px-3 h-10 dark:bg-slate-800 dark:border-slate-600">
            <BsSearch className="text-slate-400" />
            <input
              className="flex-1 h-full bg-transparent focus:outline-none dark:text-white"
              placeholder="ძებნა (ზუსტი): იუზერ ID / თრექინგი / ტელეფონი / მეილი"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && searchPacks()}
            />
          </div>
          <button
            onClick={searchPacks}
            className="rounded-md bg-teal_600 text-white text-sm font-bold px-5 h-10 hover:opacity-90"
          >
            Search
          </button>
        </div>

        {/* Unpaid parcels are hidden by default: one can only be dispatched
            with COD, and that changes who collects the money. */}
        <label className="flex items-center gap-2 text-xs dark:text-slate-200 cursor-pointer mb-3">
          <input
            type="checkbox"
            checked={includeUnpaid}
            onChange={(e) => {
              setIncludeUnpaid(e.target.checked);
              if (!e.target.checked) setCod(false);
            }}
          />
          გადაუხდელების ჩვენება
          {includeUnpaid && (
            <span className="text-amber-600">
              — გაგზავნა მხოლოდ COD-ით შეიძლება
            </span>
          )}
        </label>

        {loadingPacks ? (
          <div className="animate-pulse h-40 bg-gray-100 dark:bg-slate-800 rounded-md" />
        ) : packs.length === 0 ? (
          <p className="text-sm text-slate-500 py-4">
            {q
              ? "no packs matched — try a different query"
              : "type a tracking number, customer name, phone or email to start"}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-slate-500 border-b border-slate-200 dark:border-slate-600">
                <tr>
                  <th className="text-left py-2 pr-2">tracking</th>
                  <th className="text-left py-2 pr-2">customer</th>
                  <th className="text-left py-2 pr-2">warehouse</th>
                  <th className="text-right py-2 pr-2">weight</th>
                  <th className="text-left py-2 pr-2">status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {packs.map((p) => (
                  <tr
                    key={p.id}
                    className="border-b border-slate-100 dark:border-slate-700"
                  >
                    <td className="py-2 pr-2 font-mono dark:text-slate-200">
                      {p.tracking_number}
                    </td>
                    <td className="py-2 pr-2 dark:text-slate-200">
                      {p.user?.fname
                        ? `${p.user.fname} ${p.user.lname ?? ""}`
                        : p.user?.cname || "—"}{" "}
                      <span className="text-slate-400">
                        · {p.user?.phone || "no phone"}
                      </span>
                    </td>
                    <td className="py-2 pr-2 dark:text-slate-200">
                      #{p.abroad_office_id}
                      {p.abroad_office?.home_delivery_only ? " 🏠" : ""}
                    </td>
                    <td className="py-2 pr-2 text-right dark:text-slate-200">
                      {p.total_weight}
                    </td>
                    <td className="py-2 pr-2 dark:text-slate-200">{p.status}</td>
                    <td className="py-2">
                      <button
                        onClick={() => openSend(p)}
                        className="rounded-full bg-teal_600 text-white text-xs font-bold px-3 py-1.5 hover:opacity-90"
                      >
                        Preview & Send
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pickup / delivery dates tool (order/datesinfo) */}
      <div className="bg-white dark:bg-slate-700 rounded-xl p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <BsCalendarWeek className="text-slate-500" />
            <h2 className="font-bold dark:text-white">
              აღება / ჩაბარების თარიღები
            </h2>
          </div>
          <button
            onClick={() => loadRegions(true)}
            className="text-xs text-slate-500 hover:text-teal_600"
            title="refresh regions cache"
          >
            <BsArrowClockwise className="inline" /> ქალაქები ({regions.length})
          </button>
        </div>
        <div className="flex gap-2 flex-wrap items-end">
          <label className="text-xs flex flex-col gap-1">
            <span className="text-slate-500 dark:text-slate-400">გამგზავნი ქალაქი</span>
            <select
              className="h-9 px-3 border border-slate-300 dark:border-slate-600 rounded-md
                         dark:bg-slate-700 dark:text-white min-w-[180px]"
              value={dates.from}
              onChange={(e) => setDates((d) => ({ ...d, from: e.target.value }))}
            >
              <option value="">—</option>
              {regions.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs flex flex-col gap-1">
            <span className="text-slate-500 dark:text-slate-400">მიმღები ქალაქი</span>
            <select
              className="h-9 px-3 border border-slate-300 dark:border-slate-600 rounded-md
                         dark:bg-slate-700 dark:text-white min-w-[180px]"
              value={dates.to}
              onChange={(e) => setDates((d) => ({ ...d, to: e.target.value }))}
            >
              <option value="">—</option>
              {regions.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </label>
          <button
            onClick={runDates}
            className="rounded-md bg-teal_600 text-white text-sm font-bold px-5 h-9 hover:opacity-90"
          >
            შემოწმება
          </button>
          {dates.result && (
            <pre className="text-[11px] font-mono bg-slate-100 dark:bg-slate-800 dark:text-slate-200 p-2 rounded-md overflow-x-auto flex-1">
              {JSON.stringify(dates.result, null, 2)}
            </pre>
          )}
        </div>
      </div>

      {/* Settlement with OnWay for a period */}
      <div className="bg-white dark:bg-slate-700 rounded-xl p-4 flex flex-col gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <p className="font-bold text-[#777a83] dark:text-slate-200">ანგარიშსწორება OnWay-სთან</p>
            <p className="text-[11px] text-[#989ba5] max-w-2xl leading-snug">
              ჩაბარებული შეკვეთები ჩაბარების თარიღით. საკურიერო — რასაც OnWay გვირიცხავს; ნაღდი — რაც კურიერმა კართან აიღო ჩვენთვის.
              ციფრები OnWay-ის ჩაბარების callback-იდანაა, ანუ მათი ამონაწერის შესაბამისი.
            </p>
          </div>
          <label className="flex flex-col gap-1 text-xs text-[#777a83] dark:text-slate-300">
            დან
            <input type="date" value={settle.from} max={settle.to || undefined}
              onChange={(e) => setSettle((x) => ({ ...x, from: e.target.value }))}
              className="h-9 px-2 border border-slate-300 dark:border-slate-600 rounded-md dark:bg-slate-800 dark:text-white text-sm" />
          </label>
          <label className="flex flex-col gap-1 text-xs text-[#777a83] dark:text-slate-300">
            მდე
            <input type="date" value={settle.to} min={settle.from || undefined}
              onChange={(e) => setSettle((x) => ({ ...x, to: e.target.value }))}
              className="h-9 px-2 border border-slate-300 dark:border-slate-600 rounded-md dark:bg-slate-800 dark:text-white text-sm" />
          </label>
          <button onClick={loadSettlement} disabled={settle.busy}
            className="rounded-md bg-teal_600 disabled:opacity-50 text-white text-sm font-bold px-5 h-9 hover:opacity-90">
            {settle.busy ? "…" : "ჩვენება"}
          </button>
          {settle.data && (
            <button onClick={downloadSettlement}
              title="ჯამი, შეკვეთები და თითოეული ამანათი: კლიენტი, მისამართი, ტრანსპორტირებისა და საკურიეროს გადახდა"
              className="rounded-md bg-emerald-600 text-white text-sm font-bold px-4 h-9 hover:opacity-90">
              Excel (დეტალური)
            </button>
          )}
        </div>

        {settle.data && (
          <>
            <div className="grid grid-cols-4 md:grid-cols-2 sm:grid-cols-1 gap-3">
              <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-3">
                <p className="text-[11px] text-[#989ba5]">ჩაბარებული შეკვეთა</p>
                <p className="text-xl font-bold dark:text-white">{settle.data.delivered_count}</p>
              </div>
              <div className="rounded-lg bg-rose-50 dark:bg-rose-900/30 p-3">
                <p className="text-[11px] text-rose-700 dark:text-rose-300">OnWay-ს გადასარიცხი (საკურიერო)</p>
                <p className="text-xl font-bold text-rose-700 dark:text-rose-200">{gel(settle.data.shipping_total)}</p>
              </div>
              <div className="rounded-lg bg-teal-50 dark:bg-teal-900/30 p-3">
                <p className="text-[11px] text-teal-700 dark:text-teal-300">კურიერთან ნაღდით გადახდილი (OnWay-მ უნდა მოგვცეს)</p>
                <p className="text-xl font-bold text-teal-700 dark:text-teal-200">{gel(settle.data.cod_total)}</p>
              </div>
              <div className={`rounded-lg p-3 ${settle.data.net >= 0 ? "bg-teal-50 dark:bg-teal-900/30" : "bg-rose-50 dark:bg-rose-900/30"}`}>
                <p className="text-[11px] text-[#777a83] dark:text-slate-300">სალდო</p>
                <p className={`text-xl font-bold ${settle.data.net >= 0 ? "text-teal-700 dark:text-teal-200" : "text-rose-700 dark:text-rose-200"}`}>
                  {gel(Math.abs(settle.data.net))}
                </p>
                <p className="text-[11px] text-[#989ba5]">
                  {settle.data.net >= 0 ? "OnWay-მ უნდა გადმოგვირიცხოს" : "ჩვენ უნდა გადავურიცხოთ OnWay-ს"}
                </p>
              </div>
            </div>

            {settle.data.returned?.length > 0 && (
              <p className="text-xs text-amber-700 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-200 rounded-md px-3 py-2">
                ამ პერიოდში კურიერმა ვერ ჩააბარა / უკან დააბრუნა {settle.data.returned.length} შეკვეთა (საკურიერო {gel(settle.data.returned_shipping)}) —
                OnWay-მ შეიძლება დაბრუნებაც დაგვირიცხოს; ჯამში არ შედის: {settle.data.returned.map((r) => r.tracking).join(", ")}
              </p>
            )}

            {/* Cancelled and re-sent, yet OnWay delivered and bills both: in
                the total above, as they will bill it — ours to claim back. */}
            {settle.data.disputed?.length > 0 && (
              <p className="text-xs text-rose-700 bg-rose-50 dark:bg-rose-900/30 dark:text-rose-200 rounded-md px-3 py-2">
                სადავო {gel(settle.data.disputed_shipping)}: OnWay-მ ერთი მიტანა ორჯერ დაგვიბილა — გაუქმებული და ხელახლა გაგზავნილი შეკვეთაც ჩააბარა.
                ჯამში შედის, OnWay-სთან უნდა დავაბრუნოთ: {settle.data.disputed.map((r) => `${r.tracking} (ხელახლა: ${r.disputed})`).join(", ")}
              </p>
            )}

            <button onClick={() => setSettle((x) => ({ ...x, open: !x.open }))}
              className="self-start text-xs text-teal_600 font-bold">
              {settle.open ? "სიის დამალვა" : `სია (${settle.data.orders.length})`}
            </button>
            {settle.open && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-slate-500 border-b border-slate-200 dark:border-slate-600">
                    <tr>
                      <th className="text-left py-1.5 pr-2">ჩაბარდა</th>
                      <th className="text-left py-1.5 pr-2">OnWay</th>
                      <th className="text-left py-1.5 pr-2">კლიენტი</th>
                      <th className="text-left py-1.5 pr-2">ამანათები</th>
                      <th className="text-left py-1.5 pr-2">გადახდა</th>
                      <th className="text-right py-1.5 pr-2">საკურიერო</th>
                      <th className="text-right py-1.5 pr-2">ნაღდი</th>
                    </tr>
                  </thead>
                  <tbody>
                    {settle.data.orders.map((r) => (
                      <tr key={r.id} className="border-b border-slate-100 dark:border-slate-700 dark:text-slate-200">
                        <td className="py-1.5 pr-2 whitespace-nowrap">{r.delivered_at}</td>
                        <td className="py-1.5 pr-2 font-mono">{r.tracking}</td>
                        <td className="py-1.5 pr-2">{r.customer} <span className="text-slate-400">· {r.city}</span></td>
                        <td className="py-1.5 pr-2 font-mono break-all max-w-[260px]">{r.packs}</td>
                        <td className="py-1.5 pr-2">{r.payment}</td>
                        <td className="py-1.5 pr-2 text-right">{gel(r.shipping)}</td>
                        <td className="py-1.5 pr-2 text-right">{r.cod > 0 ? gel(r.cod) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Submitted OnWay orders */}
      <div className="bg-white dark:bg-slate-700 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <BsClipboardCheck className="text-slate-500" />
            <h2 className="font-bold dark:text-white">
              Submitted OnWay orders
              <span className="text-slate-400 font-normal"> ({ordersTotal})</span>
            </h2>
            {Object.values(picked).some(Boolean) && (
              <button
                onClick={() =>
                  setLabelIds(
                    Object.keys(picked).filter((k) => picked[k]).map(Number)
                  )
                }
                className="rounded-md bg-teal_600 text-white text-xs font-bold px-3 h-8"
              >
                მონიშნულის ბეჭდვა (
                {Object.values(picked).filter(Boolean).length})
              </button>
            )}
          </div>
          <button
            onClick={loadOrders}
            className="text-slate-500 hover:text-slate-700"
            title="refresh"
          >
            <BsArrowClockwise />
          </button>
        </div>
        {/* Filters */}
        <div className="flex flex-wrap gap-2 items-center mb-3 text-xs">
          <input
            className="h-8 px-2 border border-slate-300 dark:border-slate-600 rounded-md
                       dark:bg-slate-700 dark:text-white flex-1 min-w-[200px]"
            placeholder="ძებნა (ზუსტი): იუზერ ID / ამანათის თრექინგი / OnWay თრექინგი / ტელეფონი"
            value={orderFilters.q}
            onChange={(e) => setOrderFilters((f) => ({ ...f, q: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && loadOrders()}
          />

          <select
            className="h-8 px-2 border border-slate-300 dark:border-slate-600 rounded-md
                       dark:bg-slate-700 dark:text-white"
            value={orderFilters.state}
            onChange={(e) => setOrderFilters((f) => ({ ...f, state: e.target.value }))}
          >
            <option value="">ყველა მდგომარეობა</option>
            <option value="live">აქტიური{ordersSummary ? ` (${ordersSummary.live})` : ""}</option>
            <option value="cancelled">გაუქმებული{ordersSummary ? ` (${ordersSummary.cancelled})` : ""}</option>
            <option value="failed">ჩავარდნილი{ordersSummary ? ` (${ordersSummary.failed})` : ""}</option>
          </select>

          <select
            className="h-8 px-2 border border-slate-300 dark:border-slate-600 rounded-md
                       dark:bg-slate-700 dark:text-white"
            value={orderFilters.status}
            onChange={(e) => setOrderFilters((f) => ({ ...f, status: e.target.value }))}
          >
            <option value="">ყველა სტატუსი</option>
            {Object.entries(statusMap).map(([id, label]) => (
              <option key={id} value={id}>
                {label}
                {ordersSummary?.by_status?.[id] ? ` (${ordersSummary.by_status[id]})` : ""}
              </option>
            ))}
          </select>

          <select
            className="h-8 px-2 border border-slate-300 dark:border-slate-600 rounded-md
                       dark:bg-slate-700 dark:text-white"
            value={orderFilters.cod}
            onChange={(e) => setOrderFilters((f) => ({ ...f, cod: e.target.value }))}
          >
            <option value="">COD: ყველა</option>
            <option value="1">მხოლოდ COD{ordersSummary ? ` (${ordersSummary.cod})` : ""}</option>
            <option value="0">COD-ის გარეშე</option>
          </select>

          <input
            type="date"
            title="გაგზავნილია — დან"
            className="h-8 px-2 border border-slate-300 dark:border-slate-600 rounded-md
                       dark:bg-slate-700 dark:text-white"
            value={orderFilters.from}
            onChange={(e) => setOrderFilters((f) => ({ ...f, from: e.target.value }))}
          />
          <input
            type="date"
            title="გაგზავნილია — მდე"
            className="h-8 px-2 border border-slate-300 dark:border-slate-600 rounded-md
                       dark:bg-slate-700 dark:text-white"
            value={orderFilters.to}
            onChange={(e) => setOrderFilters((f) => ({ ...f, to: e.target.value }))}
          />

          <button
            onClick={loadOrders}
            className="h-8 px-3 rounded-md bg-teal_600 text-white font-bold"
          >
            ფილტრი
          </button>
          <button
            onClick={() =>
              setOrderFilters({ q: "", state: "", status: "", cod: "", from: "", to: "" })
            }
            className="h-8 px-3 rounded-md bg-slate-200 dark:bg-slate-600 dark:text-white"
          >
            გასუფთავება
          </button>

          <select
            className="h-8 px-2 border border-slate-300 dark:border-slate-600 rounded-md
                       bg-white dark:bg-slate-700 text-slate-800 dark:text-white text-xs"
            value={ordersSort}
            onChange={(e) => {
              setOrdersSort(e.target.value);
              setOrdersPage(1);
            }}
            title="დალაგება"
          >
            <option value="id">ახლები ჯერ</option>
            <option value="updated">ბოლოს განახლებული</option>
            <option value="status">სტატუსის ცვლილებით</option>
            <option value="sent">გაგზავნის თარიღით</option>
          </select>

          {/* Prints the whole filtered set, not the visible page — the server
              re-runs the same filter. Cancelled and failed orders are dropped
              there, so a broad filter can't fill the sheet with dead labels. */}
          <button
            onClick={() =>
              setLabelFilters(
                Object.fromEntries(
                  Object.entries(orderFilters).filter(([, v]) => v !== "" && v != null)
                )
              )
            }
            className="h-8 px-3 rounded-md bg-teal_600 text-white font-bold
                       flex items-center gap-2"
            title="ყველა გაფილტრული ეტიკეტის ბეჭდვა A4-ზე"
          >
            <BsPrinter /> ყველას ბეჭდვა
          </button>
        </div>

        {orders.length === 0 ? (
          <p className="text-sm text-slate-500 py-4">
            {Object.values(orderFilters).some((v) => v)
              ? "ამ ფილტრით შეკვეთა ვერ მოიძებნა"
              : "no orders submitted yet"}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-slate-500 border-b border-slate-200 dark:border-slate-600">
                <tr>
                  <th className="py-2 pr-2 w-8">
                    <input
                      type="checkbox"
                      checked={orders.length > 0 && orders.every((o) => picked[o.id])}
                      onChange={(e) => {
                        const next = {};
                        if (e.target.checked) orders.forEach((o) => (next[o.id] = true));
                        setPicked(next);
                      }}
                      title="ყველას მონიშვნა"
                    />
                  </th>
                  <th className="text-left py-2 pr-2">sent</th>
                  <th className="text-left py-2 pr-2">იუზერი</th>
                  <th className="text-left py-2 pr-2">ამანათები</th>
                  <th className="text-left py-2 pr-2">tracking</th>
                  <th className="text-left py-2 pr-2">status</th>
                  <th className="text-left py-2 pr-2">payment</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <Fragment key={o.id}>
                    <tr className="border-b border-slate-100 dark:border-slate-700">
                      <td className="py-2 pr-2">
                        <input
                          type="checkbox"
                          checked={!!picked[o.id]}
                          onChange={() =>
                            setPicked((p) => ({ ...p, [o.id]: !p[o.id] }))
                          }
                        />
                      </td>
                      <td className="py-2 pr-2 dark:text-slate-200">
                        {o.sent_at ? new Date(o.sent_at).toLocaleString() : "—"}
                      </td>
                      <td className="py-2 pr-2 font-mono text-teal_600">
                        {o.packs?.[0]?.user_id ? `#${o.packs[0].user_id}` : "—"}
                      </td>
                      {/* Every parcel on the order, not just the anchor. The
                          font shrinks with the count so a six-parcel order does
                          not stretch the row past the rest of the table. */}
                      <td
                        className="py-2 pr-2 font-mono dark:text-slate-200 max-w-[220px]"
                        style={{
                          fontSize:
                            (o.packs?.length || 0) > 4
                              ? "9px"
                              : (o.packs?.length || 0) > 2
                              ? "10px"
                              : "12px",
                        }}
                        title={(o.packs || []).map((p) => p.tracking_number).join("\n")}
                      >
                        {/* One order can cover 16 parcels; listing them all
                            would make that row taller than the screen. The cell
                            scrolls instead, so nothing is hidden. */}
                        <div className="max-h-[54px] overflow-y-auto pr-1">
                          {o.packs?.length
                            ? o.packs.map((p) => (
                                <div key={p.id} className="leading-tight break-all flex items-center gap-1">
                                  <span>{p.tracking_number}</span>
                                  {/* Only a live order with more than one
                                      parcel: the last parcel leaves via cancel. */}
                                  {!o.cancelled_at && o.tracking && (o.packs?.length || 0) > 1 && (
                                    <button
                                      type="button"
                                      title="შეკვეთიდან მოხსნა — პარკში არ ჩაიდო"
                                      onClick={() => removePack(o, p)}
                                      className="text-red-500 hover:text-red-700 leading-none px-0.5"
                                    >
                                      ✕
                                    </button>
                                  )}
                                </div>
                              ))
                            : `#${o.pack_id}`}
                        </div>
                        {(o.packs?.length || 0) > 3 && (
                          <div className="text-slate-400 text-[9px] mt-0.5">
                            {o.packs.length} ამანათი
                          </div>
                        )}
                      </td>
                      <td className="py-2 pr-2 font-mono dark:text-slate-200">
                        {o.tracking || "—"}
                      </td>
                      <td className="py-2 pr-2 dark:text-slate-200">
                        {statusLabel(o.onway_status_id)}
                        {o.cancelled_at && (
                          <span className="text-red-500 ml-2">CANCELLED</span>
                        )}
                        {/* OnWay's own words. The only place a failed delivery
                            is explained — without it "არ ჩაბარდა" says nothing
                            about whether to call, re-book or return. */}
                        {o.status_comment && (
                          <div className="mt-1 text-[11px] text-amber-700 bg-amber-50
                                          border border-amber-200 rounded px-1.5 py-0.5
                                          inline-block max-w-[240px]">
                            💬 {o.status_comment}
                          </div>
                        )}
                        {o.status_changed_at && (
                          <div className="text-[10px] text-slate-400 mt-0.5">
                            {String(o.status_changed_at).replace("T", " ").slice(0, 16)}
                          </div>
                        )}
                      </td>
                      <td className="py-2 pr-2 dark:text-slate-200">
                        {options?.payment_methods?.[o.payload?.payment] || "—"}
                        {" / "}
                        {options?.payers?.[o.payload?.payer] || "—"}
                        {Number(o.payload?.order_price || 0) > 0 && (
                          <span className="ml-1 text-amber-600 font-bold">
                            COD {o.payload.order_price}
                          </span>
                        )}
                      </td>
                      <td className="py-2 flex gap-1 justify-end">
                        <button
                          onClick={() => setLabelIds([o.id])}
                          className="rounded-full bg-teal_600 text-white text-xs font-bold px-3 py-1"
                          title="ეტიკეტის ბეჭდვა"
                        >
                          ეტიკეტი
                        </button>
                        <button
                          onClick={() =>
                            setExpanded(expanded === o.id ? null : o.id)
                          }
                          className="rounded-full bg-slate-200 dark:bg-slate-600 dark:text-white text-slate-700 text-xs font-bold px-3 py-1"
                        >
                          {expanded === o.id ? "hide" : "raw"}
                        </button>
                        {!o.cancelled_at && !o.tracking && (
                          <>
                            <button
                              onClick={() => resolveTracking(o)}
                              title="OnWay-მ თრექინგი არ დააბრუნა — გავარკვიოთ, რომელი შეკვეთაა, ან ჩაწერეთ პანელიდან"
                              className="rounded-full bg-amber-500 text-white text-xs font-bold px-3 py-1 hover:opacity-90"
                            >
                              თრექინგის მოძებნა
                            </button>
                            <button
                              onClick={() => closeLocally(o)}
                              title="OnWay-სთან არ არსებობს — დაიხურება მხოლოდ ჩვენს მხარეს"
                              className="rounded-full bg-slate-500 text-white text-xs font-bold px-3 py-1 hover:opacity-90"
                            >
                              არ არსებობს — დახურვა
                            </button>
                          </>
                        )}
                        {!o.cancelled_at && o.tracking && (
                          <>
                            <button
                              onClick={() => openEdit(o)}
                              className="rounded-full bg-teal_600 text-white text-xs font-bold px-3 py-1 hover:opacity-90"
                            >
                              <BsPencilSquare className="inline" /> Edit
                            </button>
                            <button
                              onClick={() => cancelOrder(o)}
                              className="rounded-full bg-red-500 text-white text-xs font-bold px-3 py-1 hover:opacity-90"
                            >
                              Cancel
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                    {expanded === o.id && (
                      <tr>
                        <td colSpan={8} className="p-2">
                          <div className="grid grid-cols-2 sm:grid-cols-1 gap-2">
                            <pre className="text-[11px] font-mono bg-slate-100 dark:bg-slate-800 dark:text-slate-200 p-2 rounded-md overflow-x-auto max-h-64">
                              payload: {JSON.stringify(o.payload, null, 2)}
                            </pre>
                            <pre className="text-[11px] font-mono bg-slate-100 dark:bg-slate-800 dark:text-slate-200 p-2 rounded-md overflow-x-auto max-h-64">
                              response: {JSON.stringify(o.response, null, 2)}
                            </pre>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>

            {ordersLastPage > 1 && (
              <div className="flex items-center gap-2 mt-3 text-xs dark:text-slate-200">
                <button
                  disabled={ordersPage <= 1}
                  onClick={() => setOrdersPage((p) => p - 1)}
                  className="px-3 h-8 rounded-md bg-slate-200 dark:bg-slate-600 disabled:opacity-40"
                >
                  ←
                </button>
                <span>
                  {ordersPage} / {ordersLastPage}
                  <span className="text-slate-400"> · {ordersTotal} შეკვეთა</span>
                </span>
                <button
                  disabled={ordersPage >= ordersLastPage}
                  onClick={() => setOrdersPage((p) => p + 1)}
                  className="px-3 h-8 rounded-md bg-slate-200 dark:bg-slate-600 disabled:opacity-40"
                >
                  →
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {(labelIds || labelFilters) && (
        <OnWayLabels
          orderIds={labelIds}
          filters={labelFilters}
          onClose={() => {
            setLabelIds(null);
            setLabelFilters(null);
          }}
        />
      )}

      {/* Send / edit modal */}
      {editor && (
        <div
          className="fixed inset-0 bg-black/60 z-[1000] flex items-center justify-center p-3"
          onClick={() => setEditor(null)}
        >
          <div
            className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold dark:text-white text-lg">
                {editor.mode === "send"
                  ? `Send to OnWay — ${editor.pack.tracking_number}`
                  : `Edit OnWay order — ${editor.order.tracking}`}
              </h2>
              <button
                onClick={() => setEditor(null)}
                className="text-slate-500 hover:text-red-500"
              >
                <BsX className="text-2xl" />
              </button>
            </div>

            {/* COD. Shown only when sending: an existing order's payment terms
                are fixed on OnWay's side once placed. */}
            {editor.mode === "send" && (
              <div
                className={`rounded-lg border p-3 mb-4 ${
                  cod
                    ? "border-amber-300 bg-amber-50"
                    : "border-slate-200 bg-slate-50 dark:bg-slate-700 dark:border-slate-600"
                }`}
              >
                <label className="flex items-center gap-2 text-sm font-bold cursor-pointer dark:text-slate-100">
                  <input
                    type="checkbox"
                    checked={cod}
                    onChange={(e) => setCod(e.target.checked)}
                  />
                  COD — თანხას კურიერი აიღებს ადგილზე
                </label>
                {cod && (
                  <p className="text-[11px] text-amber-800 mt-1">
                    ასეთი ამანათი საიტზე ვეღარ გადაიხდება — თანხა კურიერთან
                    აიღება და გადახდილად აღინიშნება ჩაბარების შემდეგ.
                    {Number(payload.order_price) > 0 && (
                      <b> ასაღები: {Number(payload.order_price).toFixed(2)} ₾</b>
                    )}
                  </p>
                )}
              </div>
            )}

            {/* Ride-alongs: the same customer's other ready parcels. Only
                offered on a new send — an existing order's contents are fixed
                on OnWay's side. */}
            {editor.mode === "send" && siblings?.siblings?.length > 0 && (
              <div className="rounded-lg border border-teal-200 bg-teal-50 p-3 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-bold text-teal-800">
                    ამ მომხმარებელს კიდევ აქვს {siblings.siblings.length} გასაგზავნი
                    ამანათი იმავე მისამართზე
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setRideAlong(
                        rideAlong.length === siblings.siblings.length
                          ? []
                          : siblings.siblings.map((x) => x.id)
                      )
                    }
                    className="text-xs font-bold text-teal_600 hover:underline"
                  >
                    {rideAlong.length === siblings.siblings.length
                      ? "მონიშვნის მოხსნა"
                      : "ყველას მონიშვნა"}
                  </button>
                </div>

                <p className="text-[11px] text-teal-700 mb-2">
                  მონიშნულები გაერთიანდება ერთ შეკვეთაში — ერთი კურიერი, ერთი
                  ტარიფი, შეჯამებული წონა.
                </p>

                <div className="flex flex-col gap-1 max-h-32 overflow-y-auto">
                  {siblings.siblings.map((sib) => (
                    <label
                      key={sib.id}
                      className="flex items-center gap-2 text-xs cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        className="w-3.5 h-3.5 shrink-0"
                        checked={rideAlong.includes(sib.id)}
                        onChange={() =>
                          setRideAlong((prev) =>
                            prev.includes(sib.id)
                              ? prev.filter((v) => v !== sib.id)
                              : [...prev, sib.id]
                          )
                        }
                      />
                      <span className="font-mono flex-1 truncate">
                        {sib.tracking_number}
                      </span>
                      <span className="text-slate-500">{sib.chargeable} კგ</span>
                      {sib.outstanding > 0 && (
                        <span className="text-amber-700 font-bold">
                          {sib.outstanding.toFixed(2)} ₾
                        </span>
                      )}
                    </label>
                  ))}
                </div>

                {rideAlong.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-teal-200 text-xs text-teal-800">
                    გაიგზავნება <b>{rideAlong.length + 1}</b> ამანათი · ჯამური წონა{" "}
                    <b>
                      {(
                        Number(siblings.anchor?.chargeable || 0) +
                        siblings.siblings
                          .filter((x) => rideAlong.includes(x.id))
                          .reduce((a, x) => a + Number(x.chargeable || 0), 0)
                      ).toFixed(2)}
                    </b>{" "}
                    კგ
                    <div className="text-[11px] mt-0.5 opacity-80">
                      წონა და თრექინგები სერვერზე ჯამდება — ქვემოთ მითითებული
                      წონა ერთი ამანათისაა.
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Parcels that are waiting but cannot share this order, so
                "why isn't X offered" has an answer on screen. */}
            {editor.mode === "send" && siblings?.excluded?.length > 0 && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 mb-4 text-[11px] text-slate-600">
                ვერ გაერთიანდება:{" "}
                {siblings.excluded.map((x) => (
                  <span key={x.id} className="font-mono mr-2">
                    {x.tracking_number} ({x.reason})
                  </span>
                ))}
              </div>
            )}

            {/* Recipient */}
            <h3 className="text-xs font-bold text-slate-400 uppercase mb-2">
              მიმღები
            </h3>
            <div className="grid grid-cols-3 sm:grid-cols-1 gap-3 mb-4">
              {field("to_name", "სახელი, გვარი *")}
              {field("to_phone", "ტელეფონი *")}
              {cityField("to_city", "ქალაქი *")}
              {field("to_address", "მისამართი *")}
              {field("to_company", "კომპანია (იურ. პირი)")}
            </div>

            {/* Sender */}
            <h3 className="text-xs font-bold text-slate-400 uppercase mb-2">
              გამგზავნი
            </h3>
            <div className="grid grid-cols-3 sm:grid-cols-1 gap-3 mb-4">
              {field("from_name", "სახელი *")}
              {field("from_phone", "ტელეფონი *")}
              {cityField("from_city", "ქალაქი *")}
              {field("from_address", "მისამართი *")}
              {field("from_company", "კომპანია")}
            </div>

            {/* Shipment */}
            <h3 className="text-xs font-bold text-slate-400 uppercase mb-2">
              ამანათი
            </h3>
            <p className="text-[11px] text-slate-500 mb-2">
              „შეფუთვების რაოდენობა" — რამდენ ცალკე ყუთს გაიტანს კურიერი.
              ნაგულისხმევად 1: ამანათებს ერთად ვფუთავთ.
            </p>
            <div className="grid grid-cols-3 sm:grid-cols-1 gap-3 mb-4">
              {field("weight", "წონა (კგ) *", { type: "number", step: "0.01" })}
              {field("quantity", "შეფუთვების რაოდენობა *", { type: "number", min: 1 })}
              {field("service_level", "მომსახურების დონე (1=სტანდარტი)", {
                type: "number",
              })}
              {field("order_number", "შეკვეთის ნომერი")}
              {field("order_detail", "კომენტარი")}
              {field("additional_information", "დამატებითი ინფორმაცია")}
              {field("invoice_amount", "დაზღვევის თანხა (ცარიელი = არ იზღვევა)")}
              <label className="text-xs flex items-center gap-2 mt-5">
                <input
                  type="checkbox"
                  checked={Number(payload.brittle) === 1}
                  onChange={(e) => patch("brittle", e.target.checked ? 1 : 0)}
                />
                <span className="dark:text-slate-300">მტვრევადი (brittle)</span>
              </label>
              <label className="text-xs flex items-center gap-2 mt-5">
                <input
                  type="checkbox"
                  checked={Number(payload.spo) === 1}
                  onChange={(e) => patch("spo", e.target.checked ? 1 : 0)}
                />
                <span className="dark:text-slate-300">spo</span>
              </label>
            </div>

            {/* Extra services */}
            <h3 className="text-xs font-bold text-slate-400 uppercase mb-2">
              დამატებითი სერვისები
            </h3>
            <div className="flex gap-4 flex-wrap mb-4">
              {Object.entries(options?.services || {}).map(([id, name]) => (
                <label key={id} className="text-xs flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={(payload.services || [])
                      .map(Number)
                      .includes(Number(id))}
                    onChange={() => toggleService(id)}
                  />
                  <span className="dark:text-slate-300">
                    {name} <span className="text-slate-400 font-mono">({id})</span>
                  </span>
                </label>
              ))}
            </div>

            {/* Payment */}
            <h3 className="text-xs font-bold text-slate-400 uppercase mb-2">
              გადახდა
            </h3>
            <div className="grid grid-cols-3 sm:grid-cols-1 gap-3 mb-2">
              {selectField("payment", "გადახდის მეთოდი *", options?.payment_methods)}
              {selectField("payer", "გადამხდელი მხარე *", options?.payers)}
              {field("order_price", "COD — ნივთების საფასური (0 = არა)", {
                type: "number",
                step: "0.01",
              })}
              <label className="text-xs flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={Number(payload.receiver_shipprice_pay) === 1}
                  onChange={(e) =>
                    patch("receiver_shipprice_pay", e.target.checked ? 1 : 0)
                  }
                />
                <span className="dark:text-slate-300">
                  გადაზიდვას იხდის მიმღები (COD-ს ემატება)
                </span>
              </label>
            </div>

            {clientErrors.length > 0 ? (
              <div className="rounded-md bg-red-50 border border-red-200 p-3 mb-4">
                {clientErrors.map((e, i) => (
                  <p key={i} className="text-xs text-red-700 flex items-start gap-2">
                    <BsExclamationTriangle className="mt-0.5 shrink-0" /> {e}
                  </p>
                ))}
              </div>
            ) : (
              // Only claim "allowed" once the catalogues are actually loaded —
              // before that there's nothing to validate against.
              options && (
                <p className="text-xs text-green-700 flex items-center gap-2 mb-4">
                  <BsCheckCircle /> გადახდის კომბინაცია დაშვებულია
                </p>
              )
            )}

            <details className="mb-4">
              <summary className="text-xs text-slate-500 cursor-pointer">
                Full JSON payload
              </summary>
              <textarea
                className="w-full h-56 mt-2 p-2 text-xs font-mono border border-slate-300
                           dark:border-slate-600 rounded-md dark:bg-slate-700 dark:text-white"
                value={JSON.stringify(editor.payload, null, 2)}
                onChange={(e) => {
                  try {
                    setEditor({ ...editor, payload: JSON.parse(e.target.value) });
                  } catch {
                    /* invalid JSON: ignore mid-typing */
                  }
                }}
              />
            </details>

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setEditor(null)}
                className="rounded-full bg-gray-200 text-slate-700 text-sm font-bold px-5 py-2"
              >
                დახურვა
              </button>
              <button
                onClick={submit}
                disabled={submitting || clientErrors.length > 0}
                className="rounded-full bg-teal_600 text-white text-sm font-bold px-5 py-2 hover:opacity-90 disabled:opacity-50"
              >
                {submitting
                  ? "იგზავნება…"
                  : editor.mode === "send"
                  ? "Send to OnWay"
                  : "შენახვა"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnWayPage;
